import { Component, OnInit, ChangeDetectorRef, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTableModule } from '@angular/material/table';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatBadgeModule } from '@angular/material/badge';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { ProjectService } from '../../shared/services/project.service';
import { EditAdminDialogComponent } from './edit-admin-dialog.component';
import { CompanyDetailsCardComponent } from './company-details-card.component';
import { AuthService } from 'app/core/auth/auth.service';

@Component({
  selector: 'app-overview',
  standalone: true,
  templateUrl: './overview.component.html',
  styleUrls: ['./overview.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatTableModule,
    MatSnackBarModule,
    MatTooltipModule,
    MatMenuModule,
    MatBadgeModule,
    MatAutocompleteModule,
    CompanyDetailsCardComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OverviewComponent implements OnInit {
  // Table properties
  displayedColumns: string[] = ['logo', 'name', 'email', 'companyName', 'createdAt', 'actions'];
  companyAdmins: any[] = []; // Original list from API
  filteredAdmins: any[] = []; // Filtered list for search dropdown
  displayAdmins: any[] = []; // List to display in table (either all or filtered)
  isLoading: boolean = false;
  isCreating: boolean = false;
  errorMessage: string = '';
  totalVersions: number = 0;
  withLogoCount: number = 0;
  withoutLogoCount: number = 0;
  
  // Search properties
  searchQuery: string = '';
  selectedAdmin: any = null;
  showSearchDropdown: boolean = false;
  isSearchActive: boolean = false; // Track if search is active
  
  // Modal properties
  showAddCompanyModal: boolean = false;
  newCompanyData = {
    name: '',
    email: '',
    companyName: ''
  };
  modalErrorMessage: string = '';

  // Delete confirmation modal
  showDeleteModal: boolean = false;
  adminToDelete: any = null;
  isDeleting: boolean = false;
  invitationActionId: string | null = null;

  // Pagination properties
  pagination = {
    page: 1,
    limit: 10,
    totalPages: 0,
    totalResults: 0
  };

  // Expanded rows
  expandedAdminId: string | null = null;

  readonly dialog = inject(MatDialog);
  private readonly authService = inject(AuthService);
  readonly canPerformDestructiveActions = this.authService.isSuperAdmin();

  constructor(
    private projectService: ProjectService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadCompanyAdmins();
  }

  loadCompanyAdmins(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.cdr.markForCheck();
    
    const timestamp = new Date().getTime();
    const queryParams = {
      page: this.pagination.page,
      limit: this.pagination.limit,
      _t: timestamp
    };
    
    this.projectService.getAllCompanyAdmins(queryParams).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        
        if (response) {
          const results = response.results || response.data || [];
          this.companyAdmins = results;
          this.filteredAdmins = [...results];
          
          // Always show all admins initially
          this.displayAdmins = [...results];
          
          this.pagination.page = response.page || 1;
          this.pagination.limit = response.limit || 10;
          this.pagination.totalPages = response.totalPages || 0;
          this.pagination.totalResults = response.totalResults || results.length;
          this.calculateStats();
        } else {
          this.companyAdmins = [];
          this.filteredAdmins = [];
          this.displayAdmins = [];
          this.resetStats();
          this.resetPagination();
        }
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.isLoading = false;
        this.errorMessage = 'Failed to load company admins. Please try again.';
        console.error('Error loading company admins:', error);
        this.resetStats();
        this.resetPagination();
        this.cdr.markForCheck();
      }
    });
  }

  // Search functionality
  onSearchInput(): void {
    if (this.searchQuery.trim() === '') {
      this.filteredAdmins = [...this.companyAdmins];
      this.selectedAdmin = null;
      this.showSearchDropdown = false;
      this.isSearchActive = false;
      this.displayAdmins = [...this.companyAdmins]; // Show all admins
      this.expandedAdminId = null;
    } else {
      const query = this.searchQuery.toLowerCase();
      this.filteredAdmins = this.companyAdmins.filter(admin => 
        admin.email.toLowerCase().includes(query) || 
        admin.name.toLowerCase().includes(query) ||
        (admin.company?.name && admin.company.name.toLowerCase().includes(query))
      );
      this.showSearchDropdown = this.filteredAdmins.length > 0;
      this.isSearchActive = false; // Not active until admin is selected
      this.displayAdmins = [...this.companyAdmins]; // Keep showing all while typing
      this.expandedAdminId = null;
    }
    this.calculateStats();
    this.cdr.markForCheck();
  }

  selectAdmin(admin: any): void {
    this.selectedAdmin = admin;
    this.searchQuery = admin.email;
    this.showSearchDropdown = false;
    this.isSearchActive = true;
    
    // Only show the selected admin in the table
    this.displayAdmins = [admin];
    
    // Auto-expand the selected admin
    this.expandedAdminId = admin._id;
    
    // Recalculate stats for filtered view
    this.calculateStats();
    
    // Scroll to the selected row
    setTimeout(() => {
      const element = document.getElementById(`admin-${admin._id}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.classList.add('bg-yellow-50');
        setTimeout(() => element.classList.remove('bg-yellow-50'), 2000);
      }
    }, 100);
    
    this.cdr.markForCheck();
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.selectedAdmin = null;
    this.filteredAdmins = [...this.companyAdmins];
    this.showSearchDropdown = false;
    this.isSearchActive = false;
    this.expandedAdminId = null;
    
    // Show all admins in table
    this.displayAdmins = [...this.companyAdmins];
    
    // Recalculate stats for full list
    this.calculateStats();
    
    this.cdr.markForCheck();
  }

  onSearchBlur(): void {
    // Use setTimeout to allow click events on dropdown items to register
    setTimeout(() => {
      this.showSearchDropdown = false;
      this.cdr.markForCheck();
    }, 200);
  }

  // Edit admin functionality
  editAdmin(admin: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    
    const dialogRef = this.dialog.open(EditAdminDialogComponent, {
      width: '500px',
      data: { admin },
      disableClose: false,
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.snackBar.open('Admin updated successfully', 'Close', { duration: 3000 });
        this.loadCompanyAdmins(); // Refresh the list
      }
    });
  }

  openAddCompanyModal(): void {
    this.showAddCompanyModal = true;
    this.modalErrorMessage = '';
    this.resetNewCompanyData();
    this.cdr.markForCheck();
  }

  closeAddCompanyModal(): void {
    this.showAddCompanyModal = false;
    this.modalErrorMessage = '';
    this.resetNewCompanyData();
    this.isCreating = false; 
    this.cdr.markForCheck();
  }

  resetNewCompanyData(): void {
    this.newCompanyData = {
      name: '',
      email: '',
      companyName: ''
    };
  }

  createNewCompany(): void {
    if (!this.newCompanyData.name.trim() || 
        !this.newCompanyData.email.trim() || 
        !this.newCompanyData.companyName.trim()) {
      this.modalErrorMessage = 'All fields are required';
      this.cdr.markForCheck();
      return;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(this.newCompanyData.email)) {
      this.modalErrorMessage = 'Please enter a valid email address';
      this.cdr.markForCheck();
      return;
    }

    this.isCreating = true;
    this.modalErrorMessage = '';
    this.cdr.markForCheck();

    this.projectService.createAdminWithCompany(this.newCompanyData).subscribe({
      next: (response: any) => {
        this.isCreating = false;

        // SUCCESS – because we are in next()
        this.snackBar.open(
          response?.message || 'Company created and secure activation invitation sent.',
          'Close',
          {
            duration: 5000,
            panelClass: ['success-snackbar']
          }
        );

        this.closeAddCompanyModal();
        this.loadCompanyAdmins();
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.isCreating = false;
        console.error('Error creating company admin:', error);

        if (error?.error?.error?.message) {
          this.modalErrorMessage = error.error.error.message;

        } else if (error?.error?.message) {
          this.modalErrorMessage = error.error.message;

        } else {
          this.modalErrorMessage = 'Failed to create company admin. Please try again.';
        }

        this.cdr.markForCheck();
      }
    });
  }

  openDeleteModal(admin: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.adminToDelete = admin;
    this.showDeleteModal = true;
    this.cdr.markForCheck();
  }

  resendInvitation(admin: any, event?: Event): void {
    event?.stopPropagation();
    this.invitationActionId = admin._id;
    this.cdr.markForCheck();
    this.projectService.resendCompanyAdminInvitation(admin._id).subscribe({
      next: (response: any) => {
        this.invitationActionId = null;
        this.snackBar.open(response?.message || 'A new secure activation invitation was sent.', 'Close', {
          duration: 4000,
          panelClass: ['success-snackbar']
        });
        this.loadCompanyAdmins();
      },
      error: (error) => {
        this.invitationActionId = null;
        this.snackBar.open(
          error?.error?.error?.message || error?.error?.message || 'Failed to resend invitation.',
          'Close',
          { duration: 4000, panelClass: ['error-snackbar'] }
        );
        this.cdr.markForCheck();
      }
    });
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.adminToDelete = null;
    this.isDeleting = false;
    this.cdr.markForCheck();
  }

  confirmDelete(): void {
    if (!this.adminToDelete) return;

    this.isDeleting = true;
    this.cdr.markForCheck();
    
    const deletingPendingInvitation = this.adminToDelete.accountStatus === 'invited';
    const request$ = deletingPendingInvitation
      ? this.projectService.revokeCompanyAdminInvitation(this.adminToDelete._id)
      : this.projectService.deleteCompanyAdmin(this.adminToDelete._id);

    request$.subscribe({
      next: (response: any) => {
        this.isDeleting = false;
        
        const isSuccess = response === null || response === undefined || 
                         response?.success || 
                         response?.status === 'success' || 
                         response?.deleted || 
                         response?.statusCode === 200 ||
                         response?.statusCode === 204;
        
        if (isSuccess) {
          this.snackBar.open(
            deletingPendingInvitation ? 'Invitation revoked successfully.' : 'Company admin deleted successfully!',
            'Close', {
            duration: 3000,
            panelClass: ['success-snackbar']
          });
          
          this.closeDeleteModal();
          this.loadCompanyAdmins(); // Refresh the list immediately
          
          if (this.companyAdmins.length === 0 && this.pagination.page > 1) {
            this.pagination.page--;
          }
        } else {
          if (response && (response.message || response.error)) {
            const errorMessage = response?.message || 
                                response?.error?.message || 
                                'Failed to delete company admin';
            this.snackBar.open(errorMessage, 'Close', {
              duration: 3000,
              panelClass: ['error-snackbar']
            });
          } else {
            this.snackBar.open('Failed to delete company admin. Please try again.', 'Close', {
              duration: 3000,
              panelClass: ['error-snackbar']
            });
          }
          this.closeDeleteModal();
        }
      },
      error: (error) => {
        this.isDeleting = false;
        console.error('Error deleting company admin:', error);
        
        let errorMessage = 'Failed to delete company admin. Please try again.';
        if (error.error?.message) {
          errorMessage = error.error.message;
        } else if (error.error?.code) {
          errorMessage = error.error.code;
        } else if (error.message) {
          errorMessage = error.message;
        } else if (error.status === 404) {
          errorMessage = 'Admin not found or already deleted.';
        }
        
        this.snackBar.open(errorMessage, 'Close', {
          duration: 3000,
          panelClass: ['error-snackbar']
        });
        this.closeDeleteModal();
        this.cdr.markForCheck();
      }
    });
  }

  private calculateStats(): void {
    // Calculate stats based on displayAdmins (either all or filtered)
    this.withLogoCount = this.displayAdmins.filter(admin => 
      admin.company && admin.company.logo && admin.company.logo.trim() !== '' && admin.company.logo !== null
    ).length;
    
    this.withoutLogoCount = this.displayAdmins.length - this.withLogoCount;
    this.totalVersions = this.displayAdmins.reduce((sum, admin) => sum + (admin.__v || 0), 0);
  }

  private resetStats(): void {
    this.totalVersions = 0;
    this.withLogoCount = 0;
    this.withoutLogoCount = 0;
  }

  private resetPagination(): void {
    this.pagination = {
      page: 1,
      limit: 10,
      totalPages: 0,
      totalResults: 0
    };
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  formatDateTime(dateString: string): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  hasLogo(admin: any): boolean {
    return admin.company && admin.company.logo && 
           admin.company.logo.trim() !== '' && 
           admin.company.logo !== null;
  }

  getLogoUrl(admin: any): string {
    if (this.hasLogo(admin)) {
      return admin.company.logo;
    }
    return '';
  }

  toggleRow(adminId: string): void {
    if (this.expandedAdminId === adminId) {
      this.expandedAdminId = null;
    } else {
      this.expandedAdminId = adminId;
    }
    this.cdr.markForCheck();
  }

  nextPage(): void {
    if (this.pagination.page < this.pagination.totalPages) {
      this.pagination.page++;
      this.loadCompanyAdmins();
    }
  }

  prevPage(): void {
    if (this.pagination.page > 1) {
      this.pagination.page--;
      this.loadCompanyAdmins();
    }
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.pagination.totalPages) {
      this.pagination.page = page;
      this.loadCompanyAdmins();
    }
  }

  copyToClipboard(text: string, message: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.snackBar.open(message, 'Close', {
        duration: 2000,
        panelClass: ['success-snackbar']
      });
    });
  }

  refreshAdmins(): void {
    this.clearSearch();
    this.loadCompanyAdmins();
  }

  manualRefresh(): void {
    this.clearSearch();
    this.pagination.page = 1;
    this.loadCompanyAdmins();
    this.cdr.markForCheck();
  }
}
